import { NgClass, NgFor, NgIf } from '@angular/common';
import { Component, HostBinding, Input, OnChanges } from '@angular/core';

import { FixtureViewType } from '@cds/betting-offer';
import { AmericanFootballScoreboardFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import {
    AmericanFootballStatistics,
    EventModel,
    EventScoreboard,
    IcehockeyPeriodGameScore,
    OrderType,
    ParticipantInfo,
    PeriodGameScore,
    TeamShootout,
    getIcehockeyPenalties,
    showPenalties,
} from '@frontend/sports/betting-offer/feature/model';
import { SitecoreEventDetailsConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { ParticipantImageComponent } from '@frontend/sports/participant-image/feature';

import { EventTimerComponent } from '../../timers/event-timer.component';
import { EventDetailsService } from '../event-details.service';
import { PenaltyShootoutComponent } from './penalty-shootout/penalty-shootout.component';
import { PeriodGameScoreboardLiveComponent } from './period-game-scoreboard-live.component';
import { ScoreCardComponent } from './score-card.component';
import { ScoreboardParticipantComponent } from './scoreboard-participant.component';

export interface PairGameScoreboardViewModel {
    score: number[];
    eventSuspended?: boolean;
    showPenalties?: boolean;
    showPenaltie: boolean;
    penalties: TeamShootout[];
}

@Component({
    selector: 'ms-period-game-scoreboard',
    templateUrl: 'period-game-scoreboard.component.html',
    imports: [
        NgClass,
        NgIf,
        NgFor,
        EventTimerComponent,
        ScoreCardComponent,
        ParticipantImageComponent,
        ScoreboardParticipantComponent,
        PeriodGameScoreboardLiveComponent,
        PenaltyShootoutComponent,
    ],
})
export class PeriodGameScoreboardComponent implements OnChanges {
    @Input() event: EventModel;
    @Input() scoreboard: EventScoreboard;
    @Input() isShowcase = false;
    @Input() showLogoColor: boolean;
    @Input() isNewEDPScoreboard = false;
    @HostBinding('class') className = 'period-game';
    @HostBinding('class.show-penaltyshootout-score') showPenaltyShootoutScore = false;
    rotateImage: boolean[] = [];
    yardDistance?: string;
    participantInfos: ParticipantInfo[];
    homeTeamTimeouts?: number;
    awayTeamTimeouts?: number;
    timeValue?: string;
    turn?: number;

    vm: PairGameScoreboardViewModel = {
        score: [0, 0],
        showPenaltie: false,
        penalties: [],
    };

    constructor(
        private americanFootballScoreboard: AmericanFootballScoreboardFactory,
        public sitecoreEventDetails: SitecoreEventDetailsConfig,
        private eventDetailsService: EventDetailsService,
    ) {}

    ngOnChanges(changes: ISimpleChanges<PeriodGameScoreboardComponent>): void {
        this.event.participants.forEach((participant, index) => {
            this.rotateImage[index] = index === 1;
        });
        const isIcehockey = this.event.sport.id === SportConstant.Icehockey;
        const icehockeyScore = isIcehockey && this.event.scoreboard.getScore<IcehockeyPeriodGameScore>()!;
        this.showPenaltyShootoutScore = icehockeyScore && showPenalties(icehockeyScore);
        const score = this.event.scoreboard.getScore<PeriodGameScore>()!;
        this.turn = score.turn;
        if (changes.scoreboard) {
            const visibleCount = this.event.viewType === FixtureViewType.American ? 3 : 5;
            this.vm = {
                score: score ? [score.participant1.total, score.participant2.total] : [0, 0],
                showPenalties: this.showPenaltyShootoutScore,
                showPenaltie: icehockeyScore && showPenalties(icehockeyScore),
                penalties: getIcehockeyPenalties(this.event, visibleCount),
                eventSuspended: this.scoreboard.period && this.scoreboard.period.id === 256,
            };
        }
        const americanFootballStats = this.scoreboard.statistics as AmericanFootballStatistics;

        this.yardDistance = this.americanFootballScoreboard.getYardDistance(americanFootballStats, this.scoreboard.period?.id);

        this.participantInfos = this.eventDetailsService.setParticipantInfos(this.event);
        if (this.event.sport.id === SportConstant.AmericanFootball) {
            this.homeTeamTimeouts = this.getTimeouts(americanFootballStats, OrderType.Team01);
            this.awayTeamTimeouts = this.getTimeouts(americanFootballStats, OrderType.Team02);
            this.timeValue = americanFootballStats.timeValue;
        }
    }

    private getTimeouts(score: AmericanFootballStatistics, teamType: OrderType): number {
        let timeoutValue = 0;
        score.timeoutsLeft.forEach((timeout) => {
            if (timeout.teamOrder === teamType) {
                if (this.scoreboard.period?.name === 'Q1' || this.scoreboard.period?.name === 'Q2') {
                    return (timeoutValue = timeout.firstHalf);
                }
                if (this.scoreboard.period?.name === 'Q3' || this.scoreboard.period?.name === 'Q4') {
                    return (timeoutValue = timeout.secondHalf);
                }
                if (this.scoreboard.period?.name === 'OT') {
                    return (timeoutValue = timeout.overtime);
                }
            }

            return;
        });

        return timeoutValue;
    }
}
